import { useForm } from "react-hook-form";
import {
  SECOND_USER_ID,
  START_DATE,
  END_DATE,
  COMMUNITY_ID,
  FIRST_USER_ID,
  BOM_POSITION_ID,
} from "./consts";
import { swapBomFormValues } from "./validations";
import { swapBomSchema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import { getRequiredFields } from "@/lib/utils";
import { SwapDialogProps } from "./types";
import {
  useGetBomAssociateUserByCommunityId,
  useTransferBomUserMutation,
} from "@/services";
import { toast } from "@/components/Toast";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useSwapDialog = ({
  swapBOMDetails,
  setSwapConfirmDialog,
}: SwapDialogProps) => {
  const [showSwapDialog, setShowSwapDialog] = useState<boolean>(false);
  const form = useForm<swapBomFormValues>({
    defaultValues: {
      [SECOND_USER_ID]: "",
      [START_DATE]: new Date(),
      [END_DATE]: "",
      [COMMUNITY_ID]: "",
    },
    resolver: zodResolver(swapBomSchema),
  });
  const queryClient = useQueryClient();

  const requiredSwapBomFields = getRequiredFields(swapBomSchema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  // // get property owners for the community
  const { data: communityUsers, isLoading: isCommunityUsersLoading } =
    useGetBomAssociateUserByCommunityId({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const onSwapConfirm = () => {
    setShowSwapDialog(true);
    setSwapConfirmDialog && setSwapConfirmDialog(false);
  };

  const { mutate: transferBOM, isPending: isTransferPending } =
    useTransferBomUserMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Transfer the BM Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bomUsersList", communityDetail?.communityId],
        });
        onCloseDialog();
      },
    });

  const onSwapSubmit = (data: swapBomFormValues) => {
    transferBOM({
      data: {
        ...data,
        [COMMUNITY_ID]: communityDetail?.communityId,
        [FIRST_USER_ID]: parseInt(swapBOMDetails?.bomUserId),
        [SECOND_USER_ID]: parseInt(data?.[SECOND_USER_ID]),
        [BOM_POSITION_ID]: parseInt(swapBOMDetails?.bomPositionId),
      },
    });
  };

  const onCloseDialog = () => {
    form.reset();
    setShowSwapDialog && setShowSwapDialog(false);
  };

  const getUniqueUserList = () =>
    communityUsers?.data?.map((user: any) => ({
      label: `${user.firstName} ${user.lastName}`,
      value: user?.userId?.toString(),
    }));

  return {
    form,
    requiredSwapBomFields,
    onCloseDialog,
    onSwapSubmit,
    isTransferPending,
    communityUsers,
    isCommunityUsersLoading,
    getUniqueUserList,
    showSwapDialog,
    setShowSwapDialog,
    onSwapConfirm,
  };
};

export default useSwapDialog;
